// Remove circular dependency - we'll handle logout directly

export interface SessionExpirationHandler {
  onSessionExpired: () => void;
}

class SessionManager {
  private static instance: SessionManager;
  private sessionExpirationHandlers: SessionExpirationHandler[] = [];

  private constructor() {
    // Set up global session expiration detection
    this.setupSessionExpirationDetection();
  }

  public static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  // Register a handler for session expiration
  public registerSessionExpirationHandler(
    handler: SessionExpirationHandler,
  ): void {
    this.sessionExpirationHandlers.push(handler);
  }

  // Unregister a handler
  public unregisterSessionExpirationHandler(
    handler: SessionExpirationHandler,
  ): void {
    const index = this.sessionExpirationHandlers.indexOf(handler);
    if (index > -1) {
      this.sessionExpirationHandlers.splice(index, 1);
    }
  }

  // Handle session expiration
  public handleSessionExpiration(): void {
    // Clear authentication data directly
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUser');

    // Notify all registered handlers
    this.sessionExpirationHandlers.forEach((handler, index) => {
      try {
        handler.onSessionExpired();
      } catch (error) {
        console.error('Error in session expiration handler:', error);
      }
    });
  }

  // Method to manually trigger session expiration (for testing)
  public triggerSessionExpiration(): void {
    this.handleSessionExpiration();
  }

  // Check if the current session is valid
  public isSessionValid(): boolean {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return false;
    }

    try {
      // Basic token validation - you might want to add more sophisticated validation
      // like checking token expiration time if your tokens include that information
      return token.length > 0;
    } catch (error) {
      console.error('Error validating session:', error);
      return false;
    }
  }

  // Setup global session expiration detection
  private setupSessionExpirationDetection(): void {
    // Check session validity periodically
    setInterval(() => {
      if (!this.isSessionValid() && !!localStorage.getItem('authToken')) {
        this.handleSessionExpiration();
      }
    }, 60000); // Check every minute

    // Also check when the window becomes visible (user returns to tab)
    document.addEventListener('visibilitychange', () => {
      if (
        !document.hidden &&
        !this.isSessionValid() &&
        !!localStorage.getItem('authToken')
      ) {
        this.handleSessionExpiration();
      }
    });
  }
}

export const sessionManager = SessionManager.getInstance();
